import React from 'react';
import SpotlightCountdown from '@/components/SpotlightCountdown/SpotlightCountdown';
import { useNavigate } from 'react-router-dom';
import { Profile } from '@/redux/slices/dashboardSlice';
import { getMediaUrl } from '@/constants/media';
import { formatName, nameInitial } from '@/utils/formatName';
import { formatPrice } from '@/utils/formatPrice';
import { profileLink, ProfileViewSource } from '@/utils/profileLink';
import './TopProfilesCarousel.scss';

interface TopProfilesCarouselProps {
  profiles: Profile[];
  title?: string;
  /** Source label attached to profile view tracking when a card is
   *  clicked. Defaults to 'top_profiles' for back-compat; the
   *  Spotlighted carousel passes 'spotlight' so ROI analytics can
   *  attribute the click to the paid promotion. */
  source?: ProfileViewSource;
}

const TopProfilesCarousel: React.FC<TopProfilesCarouselProps> = ({ profiles, title = 'Top Profiles', source = 'top_profiles' }) => {
  const navigate = useNavigate();

  const handleProfileClick = (username: string, spotlightId?: string) => {
    // When the click came from the Spotlighted carousel, pass the
    // spotlight_promotion.id as ref so analytics can credit the
    // specific paid placement (matches the marketplace card pattern).
    navigate(profileLink(username, source, source === 'spotlight' && spotlightId ? { ref: spotlightId } : {}));
  };

  return (
    <section className="top-profiles-section">
      <h2>{title}</h2>
      <div className="profiles-carousel">
        {profiles.length > 0 ? (
          profiles.map((profile) => (
            <div
              key={profile.id}
              className="profile-card"
              onClick={() => handleProfileClick(profile.creator.username, profile.id)}
            >
              <div className="profile-image">
                {profile.creator.avatar ? (
                  <img src={getMediaUrl(profile.creator.avatar) || undefined} alt={profile.creator.username} />
                ) : (
                  <div className="profile-placeholder">
                    {nameInitial({ displayName: profile.creator.displayName, username: profile.creator.username })}
                  </div>
                )}
              </div>
              <div className="profile-info">
                <div className="profile-name">
                  {formatName({ displayName: profile.creator.displayName, username: profile.creator.username })}
                </div>
                <div className="profile-price">{formatPrice(profile.priceKlv)}</div>
                {profile.favoriteCount !== undefined && profile.favoriteCount > 0 && (
                  <div className="profile-stats">
                    ⭐ {profile.favoriteCount} Klyx'd
                  </div>
                )}
                {/* "N trades today" removed — the field is populated from
                    HotScore.m_6h, a scoring-input snapshot that is only
                    correct at recompute time and drifts between refreshes.
                    On dev with stale hot_score rows the line said "1 trade
                    today" when no trade had happened in weeks. Ordering by
                    hot_score already conveys the "these are hot" signal;
                    a specific-count claim we can't back is a trust liability.
                    #380 tracks a live-count follow-up if we decide we want
                    a real number here. */}
                <SpotlightCountdown endTime={profile.spotlightEndTime} className="profile-stats" />
              </div>
            </div>
          ))
        ) : (
          <div className="no-profiles">No profiles available</div>
        )}
      </div>
    </section>
  );
};

export default TopProfilesCarousel;
